import React, { useState } from 'react';
import '@scale/frontend-lib/patch/patchChromePassiveListener';
import { ViewportRect, clampViewport } from '@scale/shared/types/ViewportRect';
interface PinchPanParams {
  zoomSensitivity?: number;
  enableUnfocusedZoom?: boolean;
  enableUnfocusedPan?: boolean;
  autofocusOnZoom?: boolean;
  stopPropagation?: boolean;
  zoomOnScroll?: boolean;
  zoomOnAlt?: boolean;
}
const DEFAULT_ZOOM_SENSITIVITY = 0.005;
const ZOOM_IN_FACTOR = 0.75;
const ZOOM_OUT_FACTOR = 1.25;

/**
 * Hook to manage viewport state and handle mouse wheel events.
 * Best used with the svg viewbox attribute.
 * Example:
 * const { viewbox, handleWheel } = usePinchPan({
 *   left: 0,
 *   top: 0,
 *   width: 100,
 *   height: 100,
 * });
 * return <svg tabIndex={0} onWheel={handleWheel} viewbox={viewbox}/>;
 * */

function usePinchPan(initialViewport: ViewportRect, opts?: PinchPanParams) {
  const {
    zoomSensitivity = DEFAULT_ZOOM_SENSITIVITY,
    enableUnfocusedZoom = false,
    enableUnfocusedPan = false,
    autofocusOnZoom = false,
    stopPropagation = true,
    zoomOnScroll = false,
    zoomOnAlt = false,
  } = opts ?? {};

  const [viewport, setViewport] = useState(initialViewport);
  const [currentInitialViewport, setNewInitialViewport] = useState(initialViewport);
  const [zoomedOutBoundingClientRect, setZoomedOutBoundingClientRect] = useState<
    DOMRect | ViewportRect
  >(initialViewport);

  const updateViewport = (focusX: number, focusY: number, zoomFactor: number) => {
    const newViewport = {
      left: focusX - (focusX - viewport.left) * zoomFactor,
      top: focusY - (focusY - viewport.top) * zoomFactor,
      width: viewport.width * zoomFactor,
      height: viewport.height * zoomFactor,
    };
    clampViewport(newViewport, initialViewport);
    setViewport(newViewport);
  };

  const setInitialViewport = (initViewport: ViewportRect) => {
    setViewport(initViewport);
    setZoomedOutBoundingClientRect(initViewport);
    setNewInitialViewport(initViewport);
  };

  const zoomOut = React.useCallback(() => {
    updateViewport(
      viewport.left + viewport.width / 2,
      viewport.top + viewport.height / 2,
      ZOOM_OUT_FACTOR,
    );
  }, [viewport]);
  const zoomIn = React.useCallback(() => {
    updateViewport(
      viewport.left + viewport.width / 2,
      viewport.top + viewport.height / 2,
      ZOOM_IN_FACTOR,
    );
  }, [viewport]);

  const handleWheel = React.useCallback(
    (e: React.WheelEvent) => {
      const target = e.currentTarget;
      const boundingClientRect = target.getBoundingClientRect();
      if (boundingClientRect.width < zoomedOutBoundingClientRect.width) {
        setZoomedOutBoundingClientRect(boundingClientRect);
      }
      const clientRect = zoomedOutBoundingClientRect ?? boundingClientRect;

      let newViewport;
      if (e.ctrlKey || zoomOnScroll || (e.altKey && zoomOnAlt)) {
        // zoom
        if (!document.activeElement?.contains(target) && !enableUnfocusedZoom) {
          return;
        }
        if (!document.activeElement?.contains(target) && autofocusOnZoom) {
          (target as any).focus();
        }

        const focusX =
          viewport.left + (viewport.width * (e.clientX - clientRect.left)) / clientRect.width;
        const focusY =
          viewport.top + (viewport.height * (e.clientY - clientRect.top)) / clientRect.height;
        const zoomFactor = Math.exp(e.deltaY * 0.005);
        newViewport = {
          left: focusX - (focusX - viewport.left) * zoomFactor,
          top: focusY - (focusY - viewport.top) * zoomFactor,
          width: viewport.width * zoomFactor,
          height: viewport.height * zoomFactor,
        };
      } else {
        // pan
        if (!document.activeElement?.contains(target) && !enableUnfocusedPan) {
          return;
        }

        const zoomFactor = viewport.height / clientRect.height;
        newViewport = {
          left: viewport.left + (e.shiftKey ? e.deltaX + e.deltaY : e.deltaX) * zoomFactor,
          top: viewport.top + (e.shiftKey ? 0 : e.deltaY) * zoomFactor,
          width: viewport.width,
          height: viewport.height,
        };
      }
      // e.preventDefault(); // broken as of react 73, difficult to fix given current structure
      if (stopPropagation) e.stopPropagation();

      clampViewport(newViewport, currentInitialViewport);
      setViewport(newViewport);
    },

    [
      initialViewport,
      viewport,
      setViewport,
      autofocusOnZoom,
      enableUnfocusedPan,
      enableUnfocusedZoom,
      stopPropagation,
      zoomSensitivity,
      zoomOnScroll,
      zoomOnAlt,
    ],
  );

  const viewbox = `${viewport.left} ${viewport.top} ${viewport.width} ${viewport.height}`;
  return { viewport, setViewport, handleWheel, viewbox, zoomOut, zoomIn, setInitialViewport };
}
export default usePinchPan;
